import { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'

export default function PatientRoster() {
  const { profile } = useAuth()
  const [patients, setPatients] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [showInvite, setShowInvite] = useState(false)
  const [inviteName, setInviteName] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviting, setInviting] = useState(false)
  const [inviteError, setInviteError] = useState('')
  const [inviteSent, setInviteSent] = useState(false)
  const navigate = useNavigate()

  useEffect(() => { fetchPatients() }, [])

  async function fetchPatients() {
    const { data } = await supabase
      .from('users')
      .select('*')
      .eq('clinic_id', profile.clinic_id)
      .eq('role', 'patient')
      .order('full_name')
    setPatients(data || [])
    setLoading(false)
  }

  async function handleInvite(e) {
    e.preventDefault()
    setInviting(true)
    setInviteError('')

    try {
      const res = await fetch('/.netlify/functions/invite-patient', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: inviteName,
          email: inviteEmail,
          clinic_id: profile.clinic_id
        })
      })

      const data = await res.json()
      if (!res.ok) { setInviteError(data.error || 'Something went wrong'); setInviting(false); return }

      setInviteSent(true)
      setInviting(false)
      fetchPatients()
    } catch (err) {
      setInviteError('Failed to send invite. Please try again.')
      setInviting(false)
    }
  }

  function resetInvite() {
    setShowInvite(false)
    setInviteName('')
    setInviteEmail('')
    setInviteError('')
    setInviteSent(false)
  }

  function getInitials(name) {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  const filtered = patients.filter(p =>
    p.full_name?.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <>
      <Navbar />
      <div className="page">
        <span onClick={() => navigate('/clinician')} className="back-link" style={{ cursor: 'pointer' }}>← Dashboard</span>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
          <div>
            <h1>Patients</h1>
            <p>{patients.length} patient{patients.length !== 1 ? 's' : ''}</p>
          </div>
          <button onClick={() => setShowInvite(true)}>+ Invite</button>
        </div>

        {patients.length > 0 && (
          <div style={{ position: 'relative', marginBottom: '1.25rem' }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', fontSize: 16, pointerEvents: 'none' }}>🔍</span>
            <input placeholder="Search patients..." value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 40 }} />
            {search && (
              <span onClick={() => setSearch('')} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</span>
            )}
          </div>
        )}

        {loading && <p>Loading...</p>}

        {!loading && patients.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon">👥</div>
            <h2 style={{ marginBottom: 8 }}>No patients yet</h2>
            <p style={{ marginBottom: '1rem' }}>Invite your first patient to get started</p>
            <button onClick={() => setShowInvite(true)}>+ Invite patient</button>
          </div>
        )}

        {!loading && patients.length > 0 && filtered.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon">🔍</div>
            <h2 style={{ marginBottom: 8 }}>No results</h2>
            <p>No patients match "{search}"</p>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(patient => (
            <div key={patient.id} className="card card-hover" onClick={() => navigate('/clinician/patients/' + patient.id)} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div className="avatar" style={{ width: 44, height: 44, fontSize: 15, flexShrink: 0 }}>
                {getInitials(patient.full_name)}
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2, fontSize: 15 }}>{patient.full_name}</p>
                <p style={{ fontSize: 12 }}>Joined {new Date(patient.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
              </div>
              <span style={{ color: 'var(--brand)', fontSize: 20 }}>→</span>
            </div>
          ))}
        </div>
      </div>

      {showInvite && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) resetInvite() }}>
          <div style={{ background: 'var(--surface)', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 640, overflow: 'hidden' }}>
            <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: 17, margin: 0 }}>Invite patient</h2>
              <button type="button" className="secondary" onClick={resetInvite} style={{ fontSize: 13, padding: '6px 12px' }}>Cancel</button>
            </div>
            <div style={{ padding: '1.5rem' }}>
              {inviteSent ? (
                <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                  <div style={{ fontSize: 48, marginBottom: '1rem' }}>📧</div>
                  <p style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 16, marginBottom: 8 }}>Invite sent!</p>
                  <p style={{ fontSize: 14, marginBottom: '1.5rem' }}>{inviteName} will receive an email to set up their account.</p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => { setInviteSent(false); setInviteName(''); setInviteEmail('') }} style={{ flex: 1 }}>Invite another</button>
                    <button className="secondary" onClick={resetInvite} style={{ flex: 1 }}>Done</button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleInvite} className="form-group">
                  {inviteError && <div className="alert-error">{inviteError}</div>}
                  <div className="form-field">
                    <label className="form-label">Patient's full name</label>
                    <input placeholder="Jane Smith" value={inviteName} onChange={e => setInviteName(e.target.value)} required />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Patient's email address</label>
                    <input type="email" placeholder="jane@example.com" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} required />
                  </div>
                  <button type="submit" disabled={inviting} style={{ padding: 14, fontSize: 15 }}>
                    {inviting ? 'Sending invite...' : 'Send invite'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
